<template>
  <v-dialog v-model="visible" persistent max-width="30vw" @input="backToParent($event)">
    <v-card class="rounded-lg">
      <v-card-title>
        {{ updateMode ? $t('users.title.update') : $t('users.title.create') }}
        <v-spacer></v-spacer>
      </v-card-title>
      <v-list-item>
        <v-list-item-content>
          <v-form ref="userForm" v-model="userFormValid" class="pa-6">
            <v-text-field
              v-if="showNameEdit"
              id="user_firstName"
              v-model="user.firstName"
              :label="$t('users.content.firstName')"
              :counter="maxCharacters"

              :disabled="updateMode"
            ></v-text-field>
            <v-text-field
              v-if="showNameEdit"
              id="user_lastName"
              v-model="user.lastName"
              :label="$t('users.content.lastName')"
              :counter="maxCharacters"
              :disabled="updateMode"
            ></v-text-field>

            <v-text-field
              id="user_email"
              v-model="user.email"
              :label="$t('users.content.email') + `*`"
              :rules="[rules.required, rules.email]"
              :disabled="updateMode"
            ></v-text-field>
            <v-switch
              v-if="!forMyself"
              id="user_status"
              v-model="switchUserStatus"
              :label="(switchUserStatus ? $t('users.content.statusEnumLong.enabled') : $t('users.content.statusEnumLong.suspended'))"
              inset
              class="mx-3"
              @change="changeUserStatus"
            ></v-switch>
            <v-switch
              v-if="isAdmin"
              id="isAdmin"
              v-model="switchUserIsAdmin"
              :label="(switchUserIsAdmin ? $t('users.content.adminEnumLong.true') : $t('users.content.adminEnumLong.false'))"
              inset
              class="mx-3"
              @change="changeUserIsAdmin"
            ></v-switch>
            <v-select
              id="user_language"
              v-model="selectedLanguage"
              :items="languageOptions"
              :label="$t('users.content.language') + `*`"
              :rules="[rules.required]"
              return-object
              prepend-icon="mdi-map"
            ></v-select>
          </v-form>
        </v-list-item-content>
      </v-list-item>
      <v-card-actions>
        <v-btn
          outlined
          class="my-4"
          color="primary" @click="backToParent(false)"
        >
          {{ updateMode ? $t('main.cancel') : $t('main.back') }}
        </v-btn>
        <v-btn
          id="create"
          color="primary"
          :disabled="!userFormValid"
          :loading="isUserUpdating"
          @click="createOrUpdateUser"
        >
          {{ updateMode ? $t('main.update') : $t('main.create') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import Role from '../../Role'
import { showToastError, showToastSuccess } from '../../event-bus'
import users from '../../services/api/users'
import CommonError from '../CommonError'
import language from './Language'
import UserStatus from './UserStatus'

export default {
  name: 'CreateOrUpdateUser',

  data() {
    return {
      visible: true,
      userFormValid: false,
      maxCharacters: 255,
      user: {
        roles: []
      },
      switchUserStatus: false,
      switchUserIsAdmin: false,
      isUserUpdating: false,
      rules: {
        required: (v) => !!v || !isNaN(parseFloat(v)) || this.$t('validation.form.requiredField'),
        minMax: (v) => (v && v.length <= this.maxCharacters) || this.$t('validation.form.mustBeLessThan', { max: this.maxCharacters }),
        email: v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,4})+$/.test(v) || this.$t('validation.form.emailMustBeValid')
      },
      languageOptions: [
        { value: language.EN, text: this.$t('users.content.languages.English') },
        { value: language.HU, text: this.$t('users.content.languages.Hungarian') }
      ],
      selectedLanguage: { value: language.EN, text: this.$t('users.content.languages.English') }
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    updateMode() {
      return this.$route.params.userId != null
    },
    forMyself() {
      return this.$route.params.userId === 'self'
    },
    showNameEdit() {
      // return this.updateMode && this.user.name && this.user.name.length > 0
      return this.updateMode
    }

  },

  async mounted() {
    if (this.updateMode) {
      this.getUser()
    } else {
      this.$set(this.user, 'status', UserStatus.SUSPENDED)
    }

    this.showSavedRoles = this.updateMode
  },

  methods: {
    async createOrUpdateUser() {
      if (!this.$refs.userForm.validate()) {
        return
      }

      this.isUserUpdating = true
      try {
        if (this.updateMode) {
          if ((this.user.name === undefined) || (this.user.name === null) || (this.user.name.length === 0)) {
            // console.debug('User name empty')
            // this.user.name = this.user.email
          }
        }
        if (this.isAdmin) {
          // handling rare case, setting default locale
          if ((this.selectedLanguage === null) || (this.selectedLanguage === undefined) || !this.selectedLanguage.value) {
            this.selectedLanguage = { value: language.EN, text: this.$t('users.content.languages.English') }
          }
          this.$set(this.user, 'locale', this.selectedLanguage.value)
        }

        this.updateMode ? await users.updateUser(this.$route.params.userId, this.user) : await users.createUser(this.user)
        showToastSuccess(this.updateMode ? this.$t('toastMessage.succesfullyUpdated') : this.$t('toastMessage.succesfullyCreated'))
        this.backToParent(false)
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if ((error.response !== undefined) && (error.response.data.error !== undefined)) {
          if (error.response.data.error === CommonError.ALREADY_EXISTS) {
            const splittedErrorMessage = error.response.data.error_description.split(' ')
            errorMsg = this.$t('toastMessage.alreadyExist', { 0: splittedErrorMessage[0] })
          } else {
            errorMsg = this.getErrorMessage(error)
          }
        } else {
          errorMsg = this.getErrorMessage(error)
        }
        showToastError(errorMsg)
      } finally {
        this.isUserUpdating = false
      }
    },

    userIsAdmin(user) {
      return this.user.roles.includes(Role.ADMIN)
    },

    changeUserStatus() {
      this.$set(this.user, 'status', this.switchUserStatus ? UserStatus.ENABLED : UserStatus.SUSPENDED)
    },

    changeUserIsAdmin() {
      if (this.switchUserIsAdmin) {
        this.user.roles = []
        this.user.roles.push('admin')
      } else {
        this.user.roles = []
      }
    },

    async getUser() {
      try {
        // this.user = this.forMyself ? await users.getUser(this.$route.params.userId) : await users.getUser(this.$route.params.userId)
        this.user = await users.getUser(this.$route.params.userId)
        this.switchUserStatus = !!this.user.status.includes(UserStatus.ENABLED)
        this.switchUserIsAdmin = !!this.userIsAdmin(this.user)
        if (this.isAdmin) {
          if (this.user.locale === language.HU) {
            this.selectedLanguage = { value: language.HU, text: this.$t('users.content.languages.Hungarian') }
          } else { // English or default by English
            this.selectedLanguage = { value: language.EN, text: this.$t('users.content.languages.English') }
          }
        }
        this.$delete(this.user, 'roles')
        this.changeUserStatus()
        this.changeUserIsAdmin()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },
    backToParent(e) {
      if (!e) {
        this.$router.go(-1)
      }
    },
    back() {
      this.$router.go(-1)
    }
  }
}
</script>

<style>
</style>
